import { ChangeDetectionStrategy, Component, inject, model, OnInit, signal, ViewEncapsulation } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepicker, MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { TranslocoModule, TranslocoService } from "@ngneat/transloco";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { firstValueFrom } from "rxjs";
import { DialogsService } from "../dialogs.service";
import { EndpointsService } from "../../modules/admin/endpoints/endpoints.service";
import { FuseConfirmationService } from "../../../@fuse/services/confirmation/confirmation.service";
import { SynchronizeBeepleWorkplaceRequest, SynchronizeBeepleWorkplaceWeekRequest } from "../../core/http/request.model";

@Component({
    selector: 'app-sync-workplace-dialog',
    templateUrl: './sync-workplace-dialog.component.html',
    styleUrls: ['./sync-workplace-dialog.component.scss'],
    imports: [FormsModule, MatButtonModule, MatDatepickerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSlideToggleModule, ReactiveFormsModule, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class SyncWorkplaceDialogComponent implements OnInit {

    public data: { beepleClientId: string, endpoint: string, workplaceCode: string, month: Date } = inject(MAT_DIALOG_DATA);
    public dialogRef = inject(MatDialogRef<SyncWorkplaceDialogComponent>);
    private dialogsService = inject(DialogsService);
    private endpointsService = inject(EndpointsService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private transloco = inject(TranslocoService);

    date = model<Date>();
    weekSync = model(false);
    minDate = signal<Date>(undefined);
    maxDate = signal<Date>(undefined);

    ngOnInit() {
        this.minDate.set(startOfMonth(this.data.month));
        this.maxDate.set(endOfMonth(this.data.month));
    }

    closeDialog() {
        this.dialogRef.close();
    }

    async accept() {
        if (!this.date()) {
            this.dialogsService.openErrorDialog({
                message: this.transloco.translate('payrollViews.dateNeededToSync')
            })
            return;
        }
        const confirm = await this.confirm();
        if (confirm) {
            if (this.weekSync()) {
                const request: SynchronizeBeepleWorkplaceWeekRequest = {
                    beepleClientId: this.data.beepleClientId,
                    endpoint: this.data.endpoint,
                    workplaceCode: this.data.workplaceCode,
                    dayOfWeek: format(this.date(), 'yyyy-MM-dd'),
                }
                try {
                    await firstValueFrom(this.endpointsService.synchronizeBeepleWorkplaceWeek(request));
                } catch (error) {
                    console.error(error);
                } finally {
                    this.closeDialog();
                }
            } else {
                const request: SynchronizeBeepleWorkplaceRequest = {
                    beepleClientId: this.data.beepleClientId,
                    endpoint: this.data.endpoint,
                    workplaceCode: this.data.workplaceCode,
                    dayOfMonth: format(this.date(), 'dd'),
                    period: format(this.data.month, 'MM'),
                    year: format(this.data.month, 'yyyy')
                }
                try {
                    await firstValueFrom(this.endpointsService.synchronizeBeepleWorkplace(request));
                } catch (error) {
                    console.error(error);
                } finally {
                    this.closeDialog();
                }
            }
        }
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    private confirm(confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate('payrollViews.confirmWorkplaceSync'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}